import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Loader2, MapPin, QrCode } from 'lucide-react';
import { Button } from '../components/ui/button';
import { QRScanner } from '../components/QRScanner';
import { Navbar } from '../components/Navbar';
import CourtConfirmationModal from '../components/CourtConfirmationModal';
import { courtAPI, seedAPI } from '../services/api';
import { useAuthStore, useSessionStore } from '../store';
import { toast } from 'sonner';

export default function QRScan() {
  const navigate = useNavigate();
  const { user, isAuthenticated, login } = useAuthStore();
  const { setSession } = useSessionStore();

  const [showScanner, setShowScanner] = useState(false);
  const [loading, setLoading] = useState(false);

  const [scannedData, setScannedData] = useState(null);
  const [clubInfo, setClubInfo] = useState(null);
  
  // New confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const activateCourtSession = async (scanPayload, validatedPayload, successMessage = 'Sesión de cancha activada') => {
    const clubLat = validatedPayload?.club?.location?.lat ?? 0;
    const clubLng = validatedPayload?.club?.location?.lng ?? 0;

    const response = await courtAPI.joinCourt({
      club_id: scanPayload.club_id,
      court_id: scanPayload.court_id,
      user_lat: clubLat,
      user_lng: clubLng,
    });

    setSession({
      club: validatedPayload.club,
      court: validatedPayload.court,
      expires_at: response.data.session_expires_at,
    });

    toast.success(response.data.encouraging_phrase || successMessage);
    navigate('/cancha');
  };

  const validateCourtContext = async (data, successMessage = 'Cancha detectada') => {
    setLoading(true);
    setScannedData(data);

    try {
      const response = await courtAPI.validateQR(data);
      setClubInfo(response.data);

      // Always show confirmation modal - don't auto-activate
      // This lets users confirm they want to start session on this court
      setShowConfirmModal(true);
      toast.success(successMessage);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error validando QR');
      setScannedData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    seedAPI.seed().catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const clubId = params.get('club_id');
    const courtId = params.get('court_id');

    if (!clubId || !courtId || scannedData) {
      return;
    }

    validateCourtContext(
      { club_id: clubId, court_id: courtId },
      'Cancha detectada desde QR'
    );
  }, [scannedData]);

  const handleOpenScanner = () => {
    setShowScanner(true);
  };

  const handleQRScan = async (data) => {
    setShowScanner(false);
    await validateCourtContext(data);
  };

  const doJoinCourt = async () => {
    if (!scannedData || !clubInfo) return;
    await activateCourtSession(scannedData, clubInfo);
  };

  const handleJoinCourt = async () => {
    if (!scannedData || !clubInfo) return;

    // Always show confirmation modal
    setShowConfirmModal(true);
  };

  // Handle confirmation from modal (after OTP if needed)
  const handleConfirmSession = async () => {
    if (!scannedData || !clubInfo) return;

    setLoading(true);
    try {
      await activateCourtSession(scannedData, clubInfo);
      setShowConfirmModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo activar la cancha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AnimatePresence>
        {showScanner && <QRScanner onScan={handleQRScan} onClose={() => setShowScanner(false)} />}
      </AnimatePresence>

      {/* New Court Confirmation Modal */}
      <CourtConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        clubInfo={clubInfo}
        onConfirm={handleConfirmSession}
        isLoading={loading}
      />

      <Navbar />

      <main className="mx-auto max-w-2xl px-4 pt-10 pb-8 md:min-h-[calc(100vh-7rem)] md:flex md:items-center md:justify-center">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full space-y-6 rounded-2xl border border-border bg-card p-6" data-testid="scan-page-card">
          <div className="space-y-2 text-left">
            <h1 className="text-4xl font-bold">Escanea el QR de tu cancha</h1>
            <p className="text-sm text-muted-foreground">Verifica tu celular una sola vez y quedas activo para guardar tus repeticiones.</p>
          </div>

          {!clubInfo && (
            <Button onClick={handleOpenScanner} className="h-12 w-full" data-testid="scan-open-scanner-button">
              <QrCode className="mr-2 h-5 w-5" />
              Escanear QR
            </Button>
          )}

          {loading && (
            <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground" data-testid="scan-loading-state">
              <Loader2 className="h-5 w-5 animate-spin" />
              Procesando...
            </div>
          )}

          {clubInfo && (
            <div className="space-y-4" data-testid="scan-club-confirmation-card">
              <div className="rounded-xl border border-border p-4">
                <p className="text-sm text-muted-foreground">Club</p>
                <p className="font-semibold">{clubInfo.club.name}</p>
                <p className="mt-2 text-sm text-muted-foreground">Cancha</p>
                <p className="font-semibold">{clubInfo.court.name}</p>
                <p className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                  <MapPin className="h-3 w-3" />
                  {clubInfo.club.location?.address || 'Ubicación del club'}
                </p>
              </div>

              <Button onClick={handleJoinCourt} disabled={loading} className="h-12 w-full" data-testid="scan-join-court-button">
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <CheckCircle2 className="mr-2 h-5 w-5" />
                    Activar cancha
                  </>
                )}
              </Button>

              <button onClick={() => { setScannedData(null); setClubInfo(null); }} className="w-full text-xs text-muted-foreground hover:text-foreground" data-testid="scan-reset-selection-button">
                Escanear otro QR
              </button>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
